/**
 * 業務ロジックと業務ルールの読取契約 — `getBusinessRules`（API 仕様 §4.2）と `getRule`。
 *
 * 業務は業務ルールの集合を持ち、順序を持たない。`rules` は業務ルールの番号の順に並べて返し、
 * 順番を表す欄は持たない。`dependencies` と `checks` は保存された値ではなく、業務ルールの入力と
 * 出力から**毎回導出する**。依存を突き合わせる計算は core（`matchRuleDependencies`）の 1 か所だけが持つ。
 *
 * 結末は `{result, reachedMark}` だけを持つ。業務をまたぐ行き先は業務フローの枝が持つので、
 * 「次の業務」は業務フローの枝から引いて `nextBusinesses` に載せる。
 *
 * **案件番号は number である**（決定 2）。面（`before` / `after`）を入力に持たないのは、
 * `before` がマスターそのものだからである。変更前は「案件を渡さない呼び出し」と同じ結果になる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1 件の業務ルールの集合・導出した依存・3 つの検査、業務ルール 1 件の中身を返す読取の契約。
 * @graph-connects none
 */
import { BUSINESS_OUTPUT_COUNTS, DECISION_TABLE_OVERLAP_POLICIES, DECISION_TABLE_RANGE_COMPARATORS, FLOW_DESTINATION_KINDS, FLOW_TYPES, FORMULA_OPERATORS, IMPLEMENTATION_KINDS, MUTATION_CRUDS, RULE_CRUDS, RULE_KINDS, SORT_DIRECTIONS, SORT_MISSING_VALUE_POLICIES, SORT_TIE_BREAKS, } from '@cortex/domain-spec-graph';
import { z } from 'zod';
import { LocaleInputShape, NodeUpdatedAtSchema, SpecElementIdSchema, UntranslatedSchema, } from './spec-graph-common.js';
/**
 * 集約の中だけで通じる番号（判定表の行・式の段・並べ替えの順位）。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 集約の中だけで通じる番号（判定表の行・式の段・並べ替えの順位）
 * @graph-connects none
 */
export const LocalElementIdSchema = z.number().int().positive();
/**
 * 案件番号（number）。`AIRCLOSET-139162` の数値部分がそのまま入る（決定 2）。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件番号（number）。`AIRCLOSET-139162` の数値部分がそのまま入る（決定 2）
 * @graph-connects none
 */
export const SpecPjNumberValueSchema = z.number().int().positive();
/**
 * 要素 1 件への参照。番号と、選んだ言語の表示名。
 *
 * 番号を返すのは、そこから別の画面へ遷移するためである。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 要素 1 件への参照。番号と、選んだ言語の表示名
 * @graph-connects none
 */
export const ElementRefSchema = z.object({
    id: SpecElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
});
/**
 * 結末の 1 行。結果の値が行の鍵で、到達の印は立てない結末では null。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 結末の 1 行（結果の値と到達の印）
 * @graph-connects none
 */
export const BusinessEndingSchema = z.object({
    result: ElementRefSchema,
    reachedMark: z.object({ property: ElementRefSchema, value: ElementRefSchema }).nullable(),
});
/**
 * 式の項 3 種。`kind` で判別する。用語の項は、足す（引く）条件 `when` を持てる（無ければ null）。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 式の項 3 種。`kind` で判別する
 * @graph-connects none
 */
export const FormulaOperandSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('term'),
        term: ElementRefSchema,
        when: z.object({ property: ElementRefSchema, value: ElementRefSchema }).nullable(),
    }),
    z.object({ kind: z.literal('step'), step: LocalElementIdSchema }),
    z.object({
        kind: z.literal('literal'),
        literal: z.number(),
        unit: ElementRefSchema.nullable(),
    }),
]);
/**
 * 判定表の範囲のセル。比べ方と、比べる相手（数値か用語）。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 判定表の範囲のセル（比べ方と、数値か用語の相手）
 * @graph-connects none
 */
export const DecisionTableRangeCellSchema = z.object({
    comparator: z.enum(DECISION_TABLE_RANGE_COMPARATORS),
    operand: z.union([z.number(), ElementRefSchema]),
});
/**
 * 判定表のセル 1 つ。値の用語か、`null`（「—」＝ その欄は見ない）か、範囲。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 判定表のセル 1 つ（値 / 見ない / 範囲）
 * @graph-connects none
 */
export const DecisionTableCellSchema = z.union([
    ElementRefSchema,
    z.null(),
    DecisionTableRangeCellSchema,
]);
/**
 * 判定表の 1 行。`cells` は入力欄と同じ数・同じ順で、見ない欄は `null`（「—」）。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 判定表の 1 行。`cells` は入力欄と同じ数・同じ順で、見ない欄は `null`（「—」）
 * @graph-connects none
 */
export const DecisionTableRowSchema = z.object({
    id: LocalElementIdSchema,
    cells: z.array(DecisionTableCellSchema),
    outputs: z.array(ElementRefSchema),
});
/**
 * 式の 1 段。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 式の 1 段（中間の名前・演算子・項）
 * @graph-connects none
 */
export const FormulaStepSchema = z.object({
    id: LocalElementIdSchema,
    name: ElementRefSchema,
    operator: z.enum(FORMULA_OPERATORS),
    operands: z.array(FormulaOperandSchema),
});
/**
 * 並べ替えのキー 1 つの決め方の中身。判定表は行ごとにキーの値 1 つを出し、出力欄を持たない。
 * 式は段だけを持つ（出す値 ＝ キー）。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 並べ替えのキーの決め方の中身（判定表 / 式）
 * @graph-connects none
 */
export const SortDeriveBodySchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('decisionTable'),
        inputs: z.array(ElementRefSchema),
        rows: z.array(z.object({
            id: LocalElementIdSchema,
            cells: z.array(DecisionTableCellSchema),
            output: ElementRefSchema,
        })),
        overlapPolicy: z.enum(DECISION_TABLE_OVERLAP_POLICIES),
    }),
    z.object({ kind: z.literal('formula'), steps: z.array(FormulaStepSchema) }),
]);
/**
 * 業務ルールの中身 4 種。形で判別する。
 *
 * 形ごとに別の形にしてあるので、判定表にしか無い欄を式の業務ルールが持つ応答を表現できない。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの中身 4 種（判定表・式・並べ替え・変更）。形で判別する
 * @graph-connects none
 */
export const RuleBodySchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('decisionTable'),
        inputs: z.array(ElementRefSchema),
        outputs: z.array(ElementRefSchema),
        rows: z.array(DecisionTableRowSchema),
        overlapPolicy: z.enum(DECISION_TABLE_OVERLAP_POLICIES),
    }),
    z.object({
        kind: z.literal('formula'),
        steps: z.array(FormulaStepSchema),
        output: ElementRefSchema,
    }),
    z.object({
        kind: z.literal('sort'),
        population: ElementRefSchema,
        keys: z.array(z.object({
            rank: LocalElementIdSchema,
            attribute: ElementRefSchema,
            // 向き（数・日時）か、値の並び（区分・状態・真偽）のどちらか
            order: z.union([z.enum(SORT_DIRECTIONS), z.array(ElementRefSchema)]),
            whenMissing: z.enum(SORT_MISSING_VALUE_POLICIES),
        })),
        tieBreak: z.enum(SORT_TIE_BREAKS),
        // 並べた先頭から出す件数。null は全件
        take: z.number().int().positive().nullable(),
        // キーの決め方。決め方の無いキーは入力の値をそのまま使う
        derive: z.array(z.object({ key: ElementRefSchema, body: SortDeriveBodySchema })),
    }),
    z.object({
        kind: z.literal('mutation'),
        target: ElementRefSchema,
        crud: z.enum(MUTATION_CRUDS),
        to: ElementRefSchema.nullable(),
    }),
]);
/**
 * 業務ルールの入力 1 件。`suppliers` はその入力を出力に持つ業務ルール（依存の相手）で、
 * 業務の入力や記録された値から読むものは空である。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの入力 1 件と、それを出力に持つ業務ルール
 * @graph-connects none
 */
export const RuleInputViewSchema = z.object({
    term: ElementRefSchema,
    suppliers: z.array(ElementRefSchema),
});
/**
 * 業務ルール 1 件の中身（業務の外でも通じる部分）。
 *
 * `readBy` はこの業務ルールの出力を入力に読む業務ルール（読まれる先）、`usedByBusinesses` は
 * この業務ルールを集合に載せている業務である。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルール 1 件の名前・形・入力・出力・中身・読まれる先・使っている業務
 * @graph-connects none
 */
export const RuleViewSchema = z.object({
    ruleId: SpecElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    kind: z.enum(RULE_KINDS),
    crud: z.enum(RULE_CRUDS),
    output: ElementRefSchema.nullable(),
    inputs: z.array(RuleInputViewSchema),
    body: RuleBodySchema,
    implementation: z.array(z.object({ kind: z.enum(IMPLEMENTATION_KINDS), ref: z.string() })),
    readBy: z.array(ElementRefSchema),
    usedByBusinesses: z.array(ElementRefSchema),
});
/**
 * 業務の中の業務ルール 1 件。中身は業務ルール 1 件（`RuleViewSchema`）と同じで、業務の中だけの欄は
 * 持たない — 業務ルールは集合なので順番の欄を持たず、結末は業務が持つ情報なので結末との関わりも
 * 業務ルールの属性にしない。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務の中の業務ルール 1 件（業務ルール 1 件の中身と同じ形）
 * @graph-connects none
 */
export const BusinessRuleViewSchema = RuleViewSchema;
/**
 * 業務の中の依存 1 件（読む業務ルール → 入力 → 出力を決める業務ルール）。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務の中の業務ルールの依存 1 件
 * @graph-connects none
 */
export const RuleDependencyViewSchema = z.object({
    readerRuleId: SpecElementIdSchema,
    supplierRuleId: SpecElementIdSchema,
    input: ElementRefSchema,
});
/**
 * 業務の中で閉じる 3 つの検査（制約 43・44・45）。0 件も答えなので必ず返す。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 依存の環・読まれない出力・宙吊りの入力の 3 つの検査
 * @graph-connects none
 */
export const BusinessRuleChecksSchema = z.object({
    cycles: z.array(z.array(SpecElementIdSchema)),
    unreadOutputs: z.array(SpecElementIdSchema),
    unsuppliedInputs: z.array(z.object({ ruleId: SpecElementIdSchema, input: ElementRefSchema })),
});
/**
 * 業務フローの枝から引いた「次の業務」1 行（この業務を通る業務フロー × 結末 × 宛先）。
 * `target` は宛先の節点が業務ならその業務で、それ以外（終了・並列・出口）は null。
 * `flowType` は業務フローの型（`main` がメイン、ほかはサブの役割）で、欄が読めなければ null。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business この業務を通る業務フローの枝 1 本（業務フローの型と結末と宛先の型と宛先の業務）
 * @graph-connects none
 */
export const BusinessNextSchema = z.object({
    flow: ElementRefSchema,
    flowType: z.enum(FLOW_TYPES).nullable(),
    result: ElementRefSchema.nullable(),
    destinationKind: z.enum(FLOW_DESTINATION_KINDS),
    target: ElementRefSchema.nullable(),
});
/**
 * `getBusinessRules` の入力。`pjNumber` を渡すとその案件の未確定の変更を重ねた姿を返す。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getBusinessRules` の入力
 * @graph-connects none
 */
export const GetBusinessRulesInputSchema = z.object({
    businessId: SpecElementIdSchema,
    pjNumber: SpecPjNumberValueSchema.optional(),
    ...LocaleInputShape,
});
/**
 * `getBusinessRules` の出力。基本・結末・業務ルール（番号の順）・依存・検査・次の業務。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getBusinessRules` の出力
 * @graph-connects none
 */
export const GetBusinessRulesOutputSchema = z.object({
    businessId: SpecElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    domain: ElementRefSchema.nullable(),
    actor: ElementRefSchema.nullable(),
    inputs: z.array(ElementRefSchema),
    executedAt: ElementRefSchema.nullable(),
    output: z.object({
        /** 出す件数。まだ決まっていない業務は null */
        count: z.enum(BUSINESS_OUTPUT_COUNTS).nullable(),
        class: ElementRefSchema.nullable(),
    }),
    endings: z.array(BusinessEndingSchema),
    rules: z.array(BusinessRuleViewSchema),
    dependencies: z.array(RuleDependencyViewSchema),
    checks: BusinessRuleChecksSchema,
    nextBusinesses: z.array(BusinessNextSchema),
});
/**
 * `getRule` の入力。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getRule` の入力
 * @graph-connects none
 */
export const GetRuleInputSchema = z.object({
    ruleId: SpecElementIdSchema,
    pjNumber: SpecPjNumberValueSchema.optional(),
    ...LocaleInputShape,
});
/**
 * 業務ルール 1 件の姿と更新時刻。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルール 1 件の姿と更新時刻
 * @graph-connects none
 */
export const RuleSnapshotSchema = RuleViewSchema.extend({ updatedAt: NodeUpdatedAtSchema });
/**
 * `getRule` の出力。master（as-is）と案件適用後（to-be）の姿。案件を渡さなければ両方同じ。
 * `before` が null なら案件が新しく作った業務ルール、`after` が null なら案件が消した業務ルール。
 * 両方 null はその番号の業務ルールが無いことを意味するので、読取は `NOT_FOUND` にする。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getRule` の出力（master の姿と案件適用後の姿）
 * @graph-connects none
 */
export const GetRuleOutputSchema = z.object({
    before: RuleSnapshotSchema.nullable(),
    after: RuleSnapshotSchema.nullable(),
});
//# sourceMappingURL=spec-graph-rule-flow.js.map